import { useCallback, useRef, useState, useEffect } from 'react';
import { useStore } from 'react-redux';
import qs from 'qs';
import { onLogout, startRequest, getSession } from '@wappsto/redux';
import useMounted from './useMounted';
import { STATUS } from '../util';

const MAX_LOG_LIMIT = 3600;

let cache: Record<string, any[]> = {};

onLogout(() => {
  cache = {};
});

interface UseNetworkStatusLogResult {
  data?: any[];
  status?: string;
}

interface GetFunArgs {
  start?: string | Date;
  end?: string | Date;
  limit?: number;
  resetCache?: boolean;
}

export default function useNetworkStatusLog(networkId: string) {
  const [{ data, status }, setResult] = useState<UseNetworkStatusLogResult>({});
  const functionRef = useRef<{ get?: (args?: GetFunArgs) => void }>({});
  const isMounted = useMounted();
  const currentRef = useRef<Record<string, any> | null>(null);
  const store = useStore();

  const getFun = ({ start, end, limit, resetCache }: GetFunArgs = {}) => {
    const getData = async (queryArg: any, initLimit: number) => {
      const newQuery = queryArg;
      const tmpData: any[] = [];
      let more = true;

      while (more && isMounted.current) {
        const state = store.getState();
        const sessionObj = getSession(state);
        const url = `/log/${networkId}/online_iot?${qs.stringify(newQuery)}`;
        const http: any = await startRequest(
          store.dispatch,
          { url, method: 'GET' },
          sessionObj,
        );
        if (!isMounted.current) {
          return [];
        }
        if (http.status !== 200) {
          throw http.json;
        }
        tmpData.push(...http.json.data);
        if (tmpData.length < initLimit) {
          more = http.json.more;
          if (more) {
            const last = tmpData.pop();
            newQuery.start = last.time;
          }
        } else {
          more = false;
        }
      }

      return tmpData;
    };

    currentRef.current = {};

    if (!networkId) {
      return;
    }
    if (resetCache) {
      delete cache[networkId];
    }
    if (cache[networkId]) {
      setResult({ status: STATUS.SUCCESS, data: cache[networkId] });
      return;
    }

    let initLimit = limit || MAX_LOG_LIMIT;
    if (initLimit > MAX_LOG_LIMIT) {
      initLimit = MAX_LOG_LIMIT;
    }

    const query: any = { limit: initLimit, order: 'descending' };
    if (start) {
      if ((start as any).constructor === Date) {
        query.start = (start as Date).toISOString();
      } else {
        query.start = start;
      }
    }
    if (end) {
      if ((end as any).constructor === Date) {
        query.end = (end as Date).toISOString();
      } else {
        query.end = end;
      }
    }

    const { current } = currentRef;

    setResult({ data: [], status: STATUS.PENDING });

    getData(query, initLimit)
      .then((response) => {
        if (isMounted.current && currentRef.current === current) {
          cache[networkId] = response;
          setResult({ status: STATUS.SUCCESS, data: response });
        }
      })
      .catch(() => {
        if (isMounted.current) {
          setResult({ status: STATUS.ERROR, data: [] });
        }
      });
  };

  useEffect(() => {
    setResult({ status: STATUS.PENDING, data: [] });
  }, [networkId]);

  functionRef.current.get = getFun;
  const get = useCallback((...args: [GetFunArgs?]) => functionRef.current.get?.(...args), []);

  return { data, status, get };
}
